import { LoaderCircle, Paperclip } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { getFormattedDate, TicketsWorkspaceText } from '../utils';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import {
  TicketAttachment,
  TicketDetail,
  TicketMessage,
} from '@/services/TicketsService';

interface TicketMessageListProps {
  selectedTicket: TicketDetail | undefined;
  isLoading: boolean;
  error: unknown;
  text: TicketsWorkspaceText;
  dateLocale: string;
  downloadingAttachmentId: number | null;
  onRetry: () => void;
  onDownloadAttachment: (attachment: TicketAttachment) => void;
  getMessageAuthorLabel: (message: TicketMessage) => string;
}

export function TicketMessageList({
  selectedTicket,
  isLoading,
  error,
  text,
  dateLocale,
  downloadingAttachmentId,
  onRetry,
  onDownloadAttachment,
  getMessageAuthorLabel,
}: TicketMessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageId =
    selectedTicket?.messages[selectedTicket.messages.length - 1]?.id;

  useEffect(() => {
    if (!lastMessageId) {
      return;
    }

    messagesEndRef.current?.scrollIntoView({ block: 'end' });
  }, [selectedTicket?.id, lastMessageId]);

  if (!selectedTicket) {
    return (
      <div className="flex h-[280px] items-center justify-center rounded-md border border-dashed text-sm text-gray-500">
        {text.selectTicketToView}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-[280px] items-center justify-center rounded-md border">
        <LoaderCircle className="h-5 w-5 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[280px] flex-col items-center justify-center gap-3 rounded-md border border-red-100 bg-red-50 p-4 text-center">
        <p className="text-sm text-red-600">{text.detailLoadFailed}</p>
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          {text.retry}
        </Button>
      </div>
    );
  }

  if (selectedTicket.messages.length === 0) {
    return (
      <div className="flex h-[280px] items-center justify-center rounded-md border border-dashed text-sm text-gray-500">
        {text.noMessages}
      </div>
    );
  }

  return (
    <div className="max-h-[420px] space-y-3 overflow-y-auto rounded-md border bg-gray-50 p-3">
      {selectedTicket.messages.map((message) => {
        const isSupportMessage = message.authorRole === 'root';

        return (
          <article
            key={message.id}
            className={cn(
              'rounded-md border p-3',
              isSupportMessage
                ? 'border-indigo-200 bg-indigo-50'
                : 'border-gray-200 bg-white',
            )}
          >
            <header className="mb-2 flex items-center justify-between gap-4">
              <strong className="text-sm text-gray-800">
                {getMessageAuthorLabel(message)}
              </strong>
              <span className="text-xs text-gray-500">
                {getFormattedDate(message.createdAt, dateLocale)}
              </span>
            </header>

            {message.messageText && (
              <p className="whitespace-pre-wrap text-sm text-gray-800">
                {message.messageText}
              </p>
            )}

            {message.attachments.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {message.attachments.map((attachment) => (
                  <Button
                    key={attachment.id}
                    size="sm"
                    variant="outline"
                    type="button"
                    disabled={downloadingAttachmentId === attachment.id}
                    onClick={() => onDownloadAttachment(attachment)}
                  >
                    {downloadingAttachmentId === attachment.id ? (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    ) : (
                      <Paperclip className="h-4 w-4" />
                    )}
                    {attachment.fileName}
                  </Button>
                ))}
              </div>
            )}
          </article>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
}
